/**
 * Member care AI chat — admin observability tab.
 *
 * List of closed support_chat rows + a side drawer with a single chat's
 * detail. No full-transcript view (per s3.3 — transcripts are not stored
 * server-side; Matt receives the full transcript via the escalation email
 * at the time of escalation).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LifeBuoy, Filter, X, ChevronRight } from 'lucide-react';
import {
  fetchSupportChats,
  fetchSupportChatDetail,
  type SupportChatListRow,
} from '../../lib/adminApi';

const RESOLUTION_LABEL: Record<string, { label: string; className: string }> = {
  resolved: { label: 'Resolved', className: 'bg-green-50 text-green-700 border-green-100' },
  // Display label only — the stored resolution VALUE stays escalated_email /
  // escalated_calendly. "Escalated (email)" was a misnomer (no email is ever sent;
  // the real team signal is the queue ticket), so the label drops the channel.
  escalated_email: { label: 'Escalated', className: 'bg-amber-50 text-amber-700 border-amber-100' },
  escalated_calendly: { label: 'Escalated (call)', className: 'bg-orange-50 text-orange-700 border-orange-100' },
  abandoned: { label: 'Abandoned', className: 'bg-gray-50 text-gray-600 border-gray-100' },
};

function resolutionBadge(resolution: string) {
  const meta = RESOLUTION_LABEL[resolution] ?? { label: resolution, className: 'bg-gray-50 text-gray-600 border-gray-100' };
  return (
    <span className={`inline-flex items-center text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-full border ${meta.className}`}>
      {meta.label}
    </span>
  );
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('en-US', { dateStyle: 'short', timeStyle: 'short' });
}

export default function AdminSupportChats() {
  const [resolutionFilter, setResolutionFilter] = useState<string>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin', 'support-chats', { resolutionFilter }],
    queryFn: () => fetchSupportChats({ resolution: resolutionFilter || undefined, limit: 50 }),
  });

  const { data: detailData } = useQuery({
    queryKey: ['admin', 'support-chats', selectedId],
    queryFn: () => fetchSupportChatDetail(selectedId!),
    enabled: !!selectedId,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <LifeBuoy className="w-6 h-6 text-primary" />
            Member Care
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Closed AI chat sessions. Full transcripts are not stored — escalations include the full transcript in the email at the time of escalation.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void refetch()}
          className="text-xs text-slate-400 hover:text-white"
        >
          Refresh
        </button>
      </div>

      {/* Filter strip */}
      <div className="bg-slate-900 rounded-xl px-3 py-2 flex items-center gap-2 flex-wrap">
        <Filter className="w-4 h-4 text-slate-500" />
        <select
          value={resolutionFilter}
          onChange={(e) => setResolutionFilter(e.target.value)}
          className="bg-slate-800 text-white text-sm px-3 py-1.5 rounded-lg border border-slate-700"
        >
          <option value="">All resolutions</option>
          <option value="resolved">Resolved</option>
          <option value="escalated_email">Escalated</option>
          <option value="escalated_calendly">Escalated (call)</option>
          <option value="abandoned">Abandoned</option>
        </select>
        {data && (
          <span className="text-xs text-slate-400 ml-auto">
            {data.total} total · {data.chats.length} shown
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-4">
        {/* List */}
        <div className="min-w-0 bg-slate-900 rounded-xl divide-y divide-slate-800">
          {isLoading && (
            <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
          )}
          {error && (
            <div className="p-8 text-center text-sm text-red-400">
              Failed to load chats: {(error as Error).message}
            </div>
          )}
          {data && data.chats.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500">
              No support chats match the current filter.
            </div>
          )}
          {data?.chats.map((row) => (
            <ChatRow
              key={row.id}
              row={row}
              selected={row.id === selectedId}
              onSelect={() => setSelectedId(row.id)}
            />
          ))}
        </div>

        {/* Detail */}
        <div className="bg-slate-900 rounded-xl p-4 lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
          {!selectedId && (
            <p className="text-sm text-slate-500 text-center py-12">
              Select a chat to see details.
            </p>
          )}
          {selectedId && !detailData && (
            <p className="text-sm text-slate-500 text-center py-12">Loading…</p>
          )}
          {detailData && (
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500">Chat ID</p>
                  <p className="text-xs font-mono text-slate-300 break-all">{detailData.chat.id}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedId(null)}
                  className="text-slate-500 hover:text-white"
                  aria-label="Close detail"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <DetailField label="Member">
                <p className="text-sm text-white break-words">{detailData.chat.member.display_name ?? '(no name)'}</p>
                <p className="text-xs text-slate-400 break-all">{detailData.chat.member.email ?? '(no email)'}</p>
                <p className="text-xs text-slate-500 break-words">Trust {detailData.chat.member.trust_score}/100 · {detailData.chat.member.total_points} pts · {detailData.chat.member.status}</p>
              </DetailField>

              <DetailField label="Issue">
                <p className="text-sm text-white">{detailData.chat.issue_type}</p>
              </DetailField>

              <DetailField label="Resolution">
                {resolutionBadge(detailData.chat.resolution)}
              </DetailField>

              <DetailField label="Summary">
                <p className="text-sm text-slate-300 leading-relaxed">
                  {detailData.chat.summary || <span className="text-slate-500 italic">(none recorded)</span>}
                </p>
              </DetailField>

              <DetailField label="Points granted">
                <p className="text-sm text-white">{detailData.chat.points_granted}</p>
              </DetailField>

              <DetailField label="Turn count">
                <p className="text-sm text-white">{detailData.chat.turn_count}</p>
              </DetailField>

              {detailData.chat.session_id && (
                <DetailField label="Session in context">
                  <p className="text-xs font-mono text-slate-300 break-all">{detailData.chat.session_id}</p>
                </DetailField>
              )}

              <DetailField label="Opened">
                <p className="text-xs text-slate-400">{formatDateTime(detailData.chat.created_at)}</p>
              </DetailField>

              {detailData.chat.closed_at && (
                <DetailField label="Closed">
                  <p className="text-xs text-slate-400">{formatDateTime(detailData.chat.closed_at)}</p>
                </DetailField>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChatRow({ row, selected, onSelect }: {
  row: SupportChatListRow;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left px-4 py-3 flex items-center justify-between gap-3 hover:bg-slate-800 transition-colors ${
        selected ? 'bg-slate-800' : ''
      }`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1">
          {resolutionBadge(row.resolution)}
          <span className="text-[10px] uppercase tracking-wide font-semibold text-slate-400">
            {row.issue_type}
          </span>
          {row.points_granted > 0 && (
            <span className="text-[10px] font-semibold text-green-400">+{row.points_granted}pt</span>
          )}
        </div>
        <p className="text-sm text-white truncate">
          {row.member.display_name ?? row.member.email ?? row.member_id}
        </p>
        <p className="text-xs text-slate-400 truncate mt-0.5">
          {row.summary || <span className="italic">(no summary)</span>}
        </p>
        <p className="text-xs text-slate-500 mt-1">
          {formatDateTime(row.created_at)} · {row.turn_count} turns
        </p>
      </div>
      <ChevronRight className="w-4 h-4 text-slate-600 flex-shrink-0" />
    </button>
  );
}

function DetailField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-500 mb-1">{label}</p>
      {children}
    </div>
  );
}
